import { useState } from "react";
import { NavArrowLeft, NavArrowRight, Refresh } from "iconoir-react";
import { lineFacts, lineSlack } from "../../shared/nonogram/lineLogic";
import type { CellMark } from "../../shared/nonogram/types";

/**
 * One line of a nonogram, stepped through a deduction.
 *
 * The point of the technique pages is that the deduction is arithmetic rather than
 * pattern recognition, so every square and every sentence below is computed from the
 * clue rather than written down beside it. A demo whose caption disagreed with its
 * own grid would teach the reader to distrust the grid, and the caption is the part a
 * writer would drift.
 *
 * All three techniques share these four steps because they are the same deduction
 * seen from different starting points: overlap starts from an empty line, edge logic
 * and forcing start from one square a crossing line already settled.
 */

type Props = {
  /** How many squares the line has. */
  length: number;
  /** The line's clues, in order. */
  clues: number[];
  /** What this particular line is here to show, above the grid. */
  caption: string;
  /** Squares a crossing line has already settled. Empty for a pure overlap line. */
  known?: CellMark[];
};

type Phase = "start" | "earliest" | "latest" | "forced";
type CellView = "plain" | "ghost" | "fill" | "cross";

const PHASES: Phase[] = ["start", "earliest", "latest", "forced"];

const PHASE_NAMES: Record<Phase, string> = {
  start: "The line as you find it",
  earliest: "The leftmost arrangement that still fits",
  latest: "The rightmost arrangement that still fits",
  forced: "What every arrangement agrees on",
};

/** Squares are counted from 1 in the prose, because that is how a player counts them
    while looking at a grid, and from 0 everywhere in the arithmetic.

    Contiguous stretches read as ranges and the rest as a list, so a line that forces
    two separate runs says "squares 5 to 8 and 10 to 15" rather than reciting ten
    numbers — and never says "squares 2 to 9" about a line where 5 is still open. */
function listSquares(indexes: number[]): string {
  const numbers = [...indexes].sort((a, b) => a - b).map((index) => index + 1);
  if (numbers.length === 0) return "no square";

  const runs: number[][] = [];
  for (const value of numbers) {
    const current = runs.at(-1);
    if (current && value === current.at(-1)! + 1) current.push(value);
    else runs.push([value]);
  }

  // A lone pair reads better as "8 and 9" than as a range of two.
  const phrases =
    runs.length === 1 && runs[0]!.length === 2
      ? runs[0]!.map(String)
      : runs.map((run) => (run.length === 1 ? `${run[0]}` : `${run[0]} to ${run.at(-1)}`));
  const joined = phrases.length === 1 ? phrases[0]! : `${phrases.slice(0, -1).join(", ")} and ${phrases.at(-1)}`;
  return `${numbers.length === 1 ? "square" : "squares"} ${joined}`;
}

function marked(cells: readonly CellMark[], mark: CellMark): number[] {
  return cells.flatMap((cell, index) => (cell === mark ? [index] : []));
}

function describeStart(length: number, clues: number[], known: CellMark[]): string {
  const slack = lineSlack(length, clues);
  const opening = `A ${length}-square line, ${clues.length === 1 ? "clue" : "clues"} ${clues.join(" ")}, slack ${slack}.`;
  const filled = marked(known, 1);
  const crossed = marked(known, -1);
  if (filled.length === 0 && crossed.length === 0) return `${opening} Nothing is known yet.`;

  const parts = [
    filled.length > 0 ? `${listSquares(filled)} ${filled.length === 1 ? "is" : "are"} filled` : "",
    crossed.length > 0 ? `${listSquares(crossed)} ${crossed.length === 1 ? "is" : "are"} crossed out` : "",
  ].filter(Boolean);
  return `${opening} A crossing line has already settled this much: ${parts.join(", and ")}.`;
}

function describeForced(length: number, clues: number[], known: CellMark[]): string {
  const facts = lineFacts(length, clues, known);
  const hasKnown = known.some((mark) => mark !== 0);
  // What this line would have given with nothing known, so the demo can say whether
  // the settled square actually bought anything — sometimes it did not.
  const alone = hasKnown ? lineFacts(length, clues) : undefined;
  const at = (cells: boolean[]) => cells.flatMap((cell, index) => (cell ? [index] : []));

  const filled = at(facts.filled);
  const empty = at(facts.empty);
  const gained =
    at(facts.filled).filter((index) => known[index] !== 1 && !alone?.filled[index]).length +
    at(facts.empty).filter((index) => known[index] !== -1 && !alone?.empty[index]).length;

  if (filled.length === 0 && empty.length === 0) {
    return `Every clue is smaller than the slack, so this line gives nothing on its own. Leave it and come back once a crossing line has settled a square.`;
  }

  // The grid draws everything the line forces; this sentence has to name the same
  // squares, or the reader trusts neither. What changed goes in the clause after it.
  const outcome = [
    filled.length > 0 ? `fills ${listSquares(filled)}` : "",
    empty.length > 0 ? `empties ${listSquares(empty)}` : "",
  ]
    .filter(Boolean)
    .join(" and ");

  if (!hasKnown) return `Every arrangement ${outcome}. Fill them in without knowing where the runs actually sit.`;
  if (gained === 0) {
    // Naming what the line forced *alone* matters here: the reader can see the
    // settled square in the grid, and calling it part of the old deduction would be
    // the one sentence on the page that is not true.
    const already = [
      alone && at(alone.filled).length > 0 ? listSquares(at(alone.filled)) : "",
      alone && at(alone.empty).length > 0 ? listSquares(at(alone.empty)) : "",
    ]
      .filter(Boolean)
      .join(" and ");
    if (already === "") return `Every arrangement ${outcome} — nothing beyond the square you were given.`;
    const plural = (alone ? at(alone.filled).length + at(alone.empty).length : 0) > 1;
    return `Every arrangement ${outcome}. ${already[0]!.toUpperCase()}${already.slice(1)} ${plural ? "were" : "was"} already forced without the settled square, so it bought you no ground.`;
  }
  return `Every arrangement ${outcome}. That is ${gained} more ${gained === 1 ? "square" : "squares"} than the line gave on its own.`;
}

function viewFor(phase: Phase, index: number, known: CellMark[], facts: ReturnType<typeof lineFacts>): CellView {
  if (phase === "start") return known[index] === 1 ? "fill" : known[index] === -1 ? "cross" : "plain";
  if (phase === "forced") return facts.filled[index] ? "fill" : facts.empty[index] ? "cross" : "plain";
  const placement = phase === "earliest" ? facts.earliest : facts.latest;
  if (placement[index]) return "ghost";
  return known[index] === -1 ? "cross" : "plain";
}

function gridLabel(phase: Phase, views: CellView[]): string {
  const at = (view: CellView) => views.flatMap((value, index) => (value === view ? [index + 1] : []));
  const parts = [
    at("fill").length > 0 ? `filled at ${at("fill").join(", ")}` : "",
    at("ghost").length > 0 ? `covered at ${at("ghost").join(", ")}` : "",
    at("cross").length > 0 ? `crossed at ${at("cross").join(", ")}` : "",
  ].filter(Boolean);
  return `${PHASE_NAMES[phase]}: ${parts.length > 0 ? parts.join("; ") : "no square marked"}, of ${views.length}.`;
}

export function LineDemo({ length, clues, caption, known }: Props) {
  const [step, setStep] = useState(0);
  const phase = PHASES[step] ?? "start";
  const settled: CellMark[] = known ?? Array.from({ length }, () => 0);
  const facts = lineFacts(length, clues, settled);

  const views = Array.from({ length }, (_, index) => viewFor(phase, index, settled, facts));
  const note =
    phase === "start"
      ? describeStart(length, clues, settled)
      : phase === "forced"
        ? describeForced(length, clues, settled)
        : facts.placements === 1
          // Both middle steps draw the same squares here, which is the point rather
          // than a glitch: nothing is left to slide.
          ? `Only one arrangement of the runs still fits, so the leftmost and the rightmost are the same one. The line is already decided.`
          : phase === "earliest"
            ? `The leftmost arrangement of the runs that still fits everything known. Nothing here is proved on its own.`
            : `The rightmost arrangement that still fits. Every legal arrangement sits somewhere between this and the previous step.`;

  const atEnd = step === PHASES.length - 1;

  return (
    <figure className="line-demo">
      <figcaption>
        <span className="eyebrow">{caption}</span>
        <span className="line-demo-clue" aria-label={`Clues ${clues.join(", ")}`}>
          {clues.map((clue, index) => <b key={index}>{clue}</b>)}
        </span>
      </figcaption>

      <div className="line-demo-track" role="img" aria-label={gridLabel(phase, views)}>
        {views.map((view, index) => (
          <span className={`line-demo-cell${view === "plain" ? "" : ` is-${view}`}`} key={index}>
            {view === "cross" ? <span aria-hidden="true">×</span> : null}
          </span>
        ))}
      </div>

      <p className="line-demo-note" role="status" aria-live="polite">{note}</p>

      <div className="line-demo-controls">
        <button
          className="tool-button"
          type="button"
          onClick={() => setStep((current) => Math.max(0, current - 1))}
          disabled={step === 0}
        >
          <NavArrowLeft width={16} height={16} strokeWidth={2.2} aria-hidden="true" /> Back
        </button>
        {/* The icon says which way the step moves; the label still carries the meaning,
            so nothing here depends on recognising the glyph. */}
        <button
          className="tool-button is-active"
          type="button"
          onClick={() => setStep((current) => (atEnd ? 0 : current + 1))}
        >
          {atEnd ? (
            <><Refresh width={16} height={16} strokeWidth={2.2} aria-hidden="true" /> Start over</>
          ) : (
            <>Next step <NavArrowRight width={16} height={16} strokeWidth={2.2} aria-hidden="true" /></>
          )}
        </button>
        <span className="line-demo-progress" aria-hidden="true">
          {PHASES.map((_, index) => <i className={index <= step ? "is-reached" : ""} key={index} />)}
        </span>
        <span className="line-demo-step">Step {step + 1} of {PHASES.length}</span>
      </div>
    </figure>
  );
}
